import { FormikProps } from 'formik'
import { useCallback, useEffect, useRef, useState } from 'react'
import { EstadoView, OptionsHelper } from '_metronic/helpers'
import { FormCheckBox, FormDateInput, FormInput, FormSelect, IOption } from 'app/components'
import { useT } from '_metronic/i18n/Metronici18n'
import { FuncionarioDadosAdicionais, FuncionarioDtoDadosAdicionais } from '../../core/_models'

/**
 * Campos de dados adicionais do profissional exigidos pelo SBIS ECF.02.01 (LO-717): nome da mãe com o
 * marcador de mãe desconhecida, UF/município/país de nascimento e naturalização. Todos opcionais.
 *
 * Usado pelo `FuncionarioModal` e pelo `ProfissionalDeSaudeModal`, que guardam esses valores dentro
 * de `funcionario.*` no formik.
 */
export type DadosAdicionaisProfissionalValues = {
  nomeMae: string
  desconheceNomeMae: boolean
  estadoNascimento?: IOption<number>
  municipioNascimento?: IOption<number>
  paisNascimento?: IOption<number>
  dtNaturalizacao: string
  portariaNaturalizacao: string
}

type FormValues = { funcionario: DadosAdicionaisProfissionalValues }

export const valoresIniciaisDadosAdicionaisProfissional = (
  dados?: FuncionarioDadosAdicionais
): DadosAdicionaisProfissionalValues => ({
  nomeMae: dados?.nomeMae || '',
  desconheceNomeMae: dados?.desconheceNomeMae === true,
  estadoNascimento: dados?.estadoNascimento
    ? { label: String(dados.estadoNascimento.descricao), value: dados.estadoNascimento.id }
    : undefined,
  municipioNascimento: dados?.municipioNascimento
    ? { label: dados.municipioNascimento.nome, value: dados.municipioNascimento.id }
    : undefined,
  paisNascimento: dados?.paisNascimento
    ? { label: dados.paisNascimento.nome, value: dados.paisNascimento.id }
    : undefined,
  dtNaturalizacao: dados?.dtNaturalizacao || '',
  portariaNaturalizacao: dados?.portariaNaturalizacao || '',
})

/** Parte do payload de dados adicionais que vem destes campos. */
export const dtoDadosAdicionaisProfissional = (
  values: DadosAdicionaisProfissionalValues
): FuncionarioDtoDadosAdicionais => ({
  nomeMae: values.desconheceNomeMae ? undefined : values.nomeMae || undefined,
  desconheceNomeMae: values.desconheceNomeMae,
  estadoNascimento: values.estadoNascimento?.value,
  municipioNascimento: values.municipioNascimento?.value,
  paisNascimento: values.paisNascimento?.value,
  dtNaturalizacao: values.dtNaturalizacao || undefined,
  portariaNaturalizacao: values.portariaNaturalizacao || undefined,
})

type IProps<V extends FormValues> = {
  formik: FormikProps<V>
  /** Estado de nascimento já gravado, para resolver a sigla antes de o combo carregar as opções. */
  estadoNascimentoInicial?: EstadoView
  idPrefix: string
}

export const DadosAdicionaisProfissionalFields = <V extends FormValues>({
  formik,
  estadoNascimentoInicial,
  idPrefix,
}: IProps<V>) => {
  const { t } = useT()
  const { setFieldValue } = formik
  const valores = formik.values.funcionario
  const idCampo = (campo: string) => `${idPrefix}-${campo}`

  // `GET /municipios/uf/{uf}` é por sigla, mas o payload grava o id do estado.
  const [siglasEstado, setSiglasEstado] = useState<Record<number, string>>(() =>
    estadoNascimentoInicial?.sigla
      ? { [estadoNascimentoInicial.id]: String(estadoNascimentoInicial.sigla) }
      : {}
  )
  const registrarEstados = useCallback((estados: EstadoView[]) => {
    setSiglasEstado((atual) => {
      const novo = { ...atual }
      ;(estados || []).forEach((estado) => {
        if (estado?.id !== undefined && estado.sigla) novo[estado.id] = String(estado.sigla)
      })
      return novo
    })
  }, [])

  const ufNascimento = valores.estadoNascimento?.value
    ? siglasEstado[Number(valores.estadoNascimento.value)]
    : undefined

  // Trocar a UF limpa o município: nunca sai um município de outro estado no payload.
  const ufAnteriorRef = useRef<string | undefined>(ufNascimento)
  useEffect(() => {
    if (ufAnteriorRef.current !== undefined && ufAnteriorRef.current !== ufNascimento) {
      setFieldValue('funcionario.municipioNascimento', undefined)
    }
    ufAnteriorRef.current = ufNascimento
  }, [ufNascimento, setFieldValue])

  const limparNomeMaeSeDesconhecido = (marcado: boolean) => {
    if (marcado) setFieldValue('funcionario.nomeMae', '')
  }

  return (
    <>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='funcionario.nomeMae'
            entity='nome_mae'
            isDisabled={valores.desconheceNomeMae || formik.isSubmitting}
          />
          <FormCheckBox
            formik={formik}
            name='funcionario.desconheceNomeMae'
            id={idCampo('desconheceNomeMae')}
            label={t('nome_mae.desconhecido')}
            className='mt-2'
            onChange={limparNomeMaeSeDesconhecido}
          />
        </div>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='funcionario.paisNascimento'
            inputId={idCampo('paisNascimento')}
            entity='pais_nascimento'
            fetchOptions={OptionsHelper.getOptionsPaises}
            isDisabled={formik.isSubmitting}
          />
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='funcionario.estadoNascimento'
            inputId={idCampo('estadoNascimento')}
            entity='estado_nascimento'
            fetchOptions={OptionsHelper.getOptionsEstadosUfNascimento}
            onOptionsLoaded={registrarEstados}
            isDisabled={formik.isSubmitting}
          />
        </div>
        <div className='col-md-6'>
          {/* Dependente da UF: sem estado fica desabilitado e não consulta a API. */}
          <FormSelect
            formik={formik}
            name='funcionario.municipioNascimento'
            inputId={idCampo('municipioNascimento')}
            entity='municipio_nascimento'
            placeholder={ufNascimento ? undefined : t('municipio_nascimento.sem_estado')}
            fetchOptions={OptionsHelper.getOptionsMunicipiosPorUf}
            fetchOptionsParams={{ uf: ufNascimento }}
            isDisabled={formik.isSubmitting || !ufNascimento}
          />
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormDateInput
            formik={formik}
            name='funcionario.dtNaturalizacao'
            entity='data_naturalizacao'
            maxDate={new Date()}
            isDisabled={formik.isSubmitting}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='funcionario.portariaNaturalizacao'
            entity='portaria_naturalizacao'
            isDisabled={formik.isSubmitting}
          />
        </div>
      </div>
    </>
  )
}
